import {
  FeedstockBmpPotential,
  FeedstockBmpPotentialPropName, FeedstockBmpType
} from '../../../../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential.model';
import { ProjectValidationUnit } from '../../../../../models/data-structures/project-validation';
import { ValidationInterface } from '../../../validation.interface';
import { FeedstockBmpPotentialDirectValidation } from './feedstock-bmp-potential-direct.validation';
import { FeedstockBmpCurveValidation } from './feedstock-bmp-curve.validation';
import { FeedstockBmpPotentialYieldValidation } from './feedstock-bmp-potential-yield.validation';
import { FeedstockBmpPotentialCodValidation } from './feedstock-bmp-potential-cod.validation';
import { FeedstockBmpPotentialNutritionValidation } from './feedstock-bmp-potential-nutrition.validation';
import { FeedstockBmpPotentialElementalValidation } from './feedstock-bmp-potential-elemental.validation';
import { FeedstockBmpPotentialAI1Validation } from './feedstock-bmp-potential-ai1.validation';
import { FeedstockBmpPotentialAI2Validation } from './feedstock-bmp-potential-ai2.validation';

export class FeedstockBmpPotentialValidation implements ValidationInterface {
  /** the BMP potential obj that is being validated */
  bmpPotential: FeedstockBmpPotential;
  /**
   * Determines if the feedstock will use database values, this alters what inputs are required for the project
   * to be valid.
   */
  useDatabase: boolean;

  /**
   * Sets the BMP potential obj that will be used for validation
   * @param bmpPotential will be used to set the current BMP potential obj
   * @param useDatabase is a boolean that determines if database values will be used to make
   * some inputs optional.
   */
  constructor(bmpPotential: FeedstockBmpPotential, useDatabase: boolean) {
    if (!bmpPotential) throw Error('invalid BMP potential provided for validation');
    this.useDatabase = useDatabase;
    this.bmpPotential = new FeedstockBmpPotential(bmpPotential);
  }

  /**
   * Get a validation unit containing the validation data for the current BMP potential obj
   */
  getValidation(): ProjectValidationUnit {
    if (!this.bmpPotential) throw Error('unable to validate BMP potential');
    const validationUnit = new ProjectValidationUnit();
    if (!this.useDatabase || (this.bmpPotential)) {
      if (this.bmpPotential.type) {
        validationUnit.setValid(FeedstockBmpPotentialPropName.type);
      } else {
        validationUnit.setInvalid(
          FeedstockBmpPotentialPropName.type,
          [FeedstockBmpPotentialErrors.MISSING_BMP_TYPE]
        );
      }
      switch (this.bmpPotential.type) {
        case FeedstockBmpType.BMP_DIRECT:
          new FeedstockBmpPotentialDirectValidation(this.bmpPotential.bmpDirect).getValidation(validationUnit);
          break;
        case FeedstockBmpType.BMP_CURVE:
          new FeedstockBmpCurveValidation(this.bmpPotential.bmpCurve).getValidation(validationUnit);
          break;
        case FeedstockBmpType.BIOGAS_YIELD:
          new FeedstockBmpPotentialYieldValidation(this.bmpPotential.bmpYield).getValidation(validationUnit);
          break;
        case FeedstockBmpType.COD:
          new FeedstockBmpPotentialCodValidation(this.bmpPotential.bmpCod).getValidation(validationUnit);
          break;
        case FeedstockBmpType.NUTRIENT_COMPOSITION:
          new FeedstockBmpPotentialNutritionValidation(this.bmpPotential.bmpNutrient).getValidation(validationUnit);
          break;
        case FeedstockBmpType.ELEMENTAL:
          new FeedstockBmpPotentialElementalValidation(this.bmpPotential.bmpElemental).getValidation(validationUnit);
          break;
        case FeedstockBmpType.AI1:
          new FeedstockBmpPotentialAI1Validation(this.bmpPotential.bmpAI1).getValidation(validationUnit);
          break;
        case FeedstockBmpType.AI2:
          new FeedstockBmpPotentialAI2Validation(this.bmpPotential.bmpAI2).getValidation(validationUnit);
          break;
      }
    }
    return validationUnit;
  }

  /**
   * returns a boolean that determines if the current BMP potential is valid or not
   */
  isValid(): boolean {
    return this.getValidation().isValid;
  }
}

/**
 * An enum that contains errors for BMP potential validation
 */
enum FeedstockBmpPotentialErrors {
  MISSING_BMP_TYPE = 'VALIDATION.MISSING_BMP_TYPE',
}
